/**
 * Upload-Context — der Upload-Flow als app-weiter Provider (2026-06-16 aus dem
 * conditional-gerenderten UploadModal in einen dauerhaft gemounteten
 * `UploadProvider` gehoben). Damit überlebt ein laufender Upload sowohl das
 * Minimieren-zu-Toast als auch SPA-Navigation (der Provider lebt im Layout,
 * das die ganze authed Session gemountet bleibt).
 *
 * Bewusst ZWEI Contexts aus einem Provider (Re-Render-Hygiene, Session-23-Lehre,
 * `patterns/react.md`):
 *  - `UploadControlContext` — `view` (selten ändernd) + die STABILEN Callbacks
 *    (open/minimize/restore/requestClose). Konsumenten (Topbar/Empty-State-CTAs)
 *    re-rendern NICHT pro Progress-Tick.
 *  - `UploadEngineContext` — `files`/`fields`/`submitting`/Setter/submit/retry/cancel.
 *    Heavy, ändert pro XHR-Progress-Tick → nur die View (Modal/Toast) hängt dran.
 *
 * `useUploadModal()` (Control) bleibt rückwärtskompatibel zum früheren
 * LayoutContext-Hook (`{ isOpen, open, close, minimize }`) + neu `restore`.
 * NOOP-Fallback-Pattern wie `layout-context.tsx` (Hook außerhalb Provider →
 * no-op statt throw; LoginPage, isolierte Tests).
 */
import { createContext, useContext, type Dispatch, type SetStateAction } from 'react';
import type { FacetIdsInput } from '../../shared/vocab-types';
import type { RenamePattern } from '../../shared/rename-pattern';

/** Eine Datei in der Upload-Queue (Status + Progress). Lebte früher in
 *  UploadModal.tsx, wandert mit dem Engine-Lift hierher (Single-Source). */
export type PendingFile = {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  progress: number;
  error?: string;
  assetId?: string;
};

/** Die pro-Request mitgeschickten Batch-Felder (Snapshot zum Submit-Zeitpunkt,
 *  C5). Lebte früher in UploadModal.tsx. */
export type UploadFields = {
  tags: string[];
  facets: FacetIdsInput;
  displayName?: string;
  recipeId?: string | null;
};

/** Anzeige-Zustand des Upload-Flows. Ein Flow hat genau einen View-State. */
export type UploadView = 'closed' | 'modal' | 'toast';

// ── Control-Context — stabile Callbacks + selten ändernder view-Enum ──────
export interface UploadControlApi {
  /** Aktueller Anzeige-Zustand. `closed` | `modal` | `toast`. */
  view: UploadView;
  /** Öffnet das Modal (oder restored, falls bereits ein Upload toast-minimiert
   *  läuft — der in-flight-Batch wird nicht geclobbert, Recipe-Arg dann ignoriert). */
  open: (recipe?: { id: string; name: string }) => void;
  /** Minimiert das Modal zum Toast (Upload läuft im Provider weiter). */
  minimize: () => void;
  /** Stellt das Modal aus dem Toast wieder her (State unverändert). */
  restore: () => void;
  /** X/Esc/Backdrop-Pfad: submitting → Cancel-Confirm; sonst close + reset. */
  requestClose: () => void;
}

const NOOP_CONTROL: UploadControlApi = {
  view: 'closed',
  open: () => {
    /* no-op — Hook außerhalb UploadProvider */
  },
  minimize: () => {
    /* no-op */
  },
  restore: () => {
    /* no-op */
  },
  requestClose: () => {
    /* no-op */
  },
};

export const UploadControlContext = createContext<UploadControlApi | null>(null);

// ── Engine-Context — heavy State + Engine-Funktionen (pro Tick ändernd) ───
export interface UploadEngineApi {
  files: PendingFile[];
  tagNames: string[];
  setTagNames: Dispatch<SetStateAction<string[]>>;
  facets: FacetIdsInput;
  setFacets: Dispatch<SetStateAction<FacetIdsInput>>;
  recipeId: string | null;
  recipeName: string | null;
  recipeLocked: boolean;
  setRecipe: (id: string | null, name: string | null) => void;
  renameEnabled: boolean;
  setRenameEnabled: Dispatch<SetStateAction<boolean>>;
  handleRenameChange: (pattern: RenamePattern, meaningful: boolean) => void;
  renamedNames: Map<string, string>;
  submitting: boolean;
  addFiles: (accepted: File[]) => void;
  removeFile: (id: string) => void;
  resetAll: () => void;
  handleSubmit: () => Promise<void>;
  retryFile: (id: string) => Promise<void>;
}

const NOOP_ENGINE: UploadEngineApi = {
  files: [],
  tagNames: [],
  setTagNames: () => {},
  facets: { produktIds: [], verwendungIds: [] },
  setFacets: () => {},
  recipeId: null,
  recipeName: null,
  recipeLocked: false,
  setRecipe: () => {},
  renameEnabled: false,
  setRenameEnabled: () => {},
  handleRenameChange: () => {},
  renamedNames: new Map(),
  submitting: false,
  addFiles: () => {},
  removeFile: () => {},
  resetAll: () => {},
  handleSubmit: async () => {},
  retryFile: async () => {},
};

export const UploadEngineContext = createContext<UploadEngineApi | null>(null);

let warnedControlOnce = false;
let warnedEngineOnce = false;

/** Control-Accessor — rückwärtskompatibel zum früheren LayoutContext-Hook
 *  (`open`/`close`/`isOpen`) + neu `minimize`/`restore`. Außerhalb des Providers
 *  no-ops (LoginPage, Tests). */
export function useUploadModal(): {
  isOpen: boolean;
  open: (recipe?: { id: string; name: string }) => void;
  close: () => void;
  minimize: () => void;
  restore: () => void;
} {
  const ctx = useContext(UploadControlContext);
  const api = ctx ?? NOOP_CONTROL;
  if (!ctx && import.meta.env.DEV && !warnedControlOnce) {
    warnedControlOnce = true;
    console.warn(
      '[upload-context] useUploadModal used outside <UploadProvider> — returning no-op. ' +
        'If this is in a page rendered by App.tsx authed-branch, the Provider ordering is broken.',
    );
  }
  return {
    isOpen: api.view !== 'closed',
    open: api.open,
    // `close` (Kompat-Name) routet über requestClose → Confirm wenn submitting.
    close: api.requestClose,
    minimize: api.minimize,
    restore: api.restore,
  };
}

/** Schmaler Accessor nur auf den `view`-Enum — für den View-Switch im Layout.
 *  Hängt am Control-Context → re-rendert nur bei view-Wechsel, nicht pro Tick. */
export function useUploadView(): UploadView {
  return (useContext(UploadControlContext) ?? NOOP_CONTROL).view;
}

/** Voller Control-Accessor (view + open/minimize/restore/requestClose) — für die
 *  View-Komponenten (UploadModal/UploadToast), die `minimize`/`restore`/`requestClose`
 *  direkt brauchen. Außerhalb des Providers no-op. */
export function useUploadControl(): UploadControlApi {
  return useContext(UploadControlContext) ?? NOOP_CONTROL;
}

/** Engine-Accessor — für die View-Komponenten (UploadModal/UploadToast).
 *  Außerhalb des Providers no-op-State (Tests). */
export function useUploadEngine(): UploadEngineApi {
  const ctx = useContext(UploadEngineContext);
  if (ctx) return ctx;
  if (import.meta.env.DEV && !warnedEngineOnce) {
    warnedEngineOnce = true;
    console.warn(
      '[upload-context] useUploadEngine used outside <UploadProvider> — returning no-op.',
    );
  }
  return NOOP_ENGINE;
}
